const EXCEL_MINUTES_IN_DAY = 24 * 60;

const padTimePart = (value: number): string =>
  value.toString().padStart(2, "0");

const toMeridiemTime = (
  hours24: number,
  minutes: number,
): string | undefined => {
  if (!Number.isInteger(hours24) || !Number.isInteger(minutes))
    return undefined;
  if (hours24 < 0 || hours24 > 23 || minutes < 0 || minutes > 59)
    return undefined;

  const meridiem = hours24 >= 12 ? "PM" : "AM";
  const displayHour = hours24 % 12 || 12;

  return `${padTimePart(displayHour)}:${padTimePart(minutes)} ${meridiem}`;
};

const parseExcelTime = (value: number): string | undefined => {
  if (!Number.isFinite(value)) return undefined;

  const fractionalDay = ((value % 1) + 1) % 1;
  const totalMinutes =
    Math.round(fractionalDay * EXCEL_MINUTES_IN_DAY) % EXCEL_MINUTES_IN_DAY;
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return toMeridiemTime(hours24, minutes);
};

export const normalizeGroundTimeValue = (
  value: unknown,
): string | undefined => {
  if (value == null || value === "") return undefined;

  if (typeof value === "number") {
    return parseExcelTime(value);
  }

  const raw = String(value).trim();
  if (!raw) return undefined;

  if (/^\d+(\.\d+)?$/.test(raw)) {
    const numericValue = Number(raw);
    if (!Number.isNaN(numericValue)) {
      return parseExcelTime(numericValue);
    }
  }

  const compactMeridiemMatch = raw.match(
    /^(\d{1,2})(?::(\d{2}))?\s*([APap][Mm])$/,
  );
  if (compactMeridiemMatch) {
    const hours12 = Number(compactMeridiemMatch[1]);
    const minutes = Number(compactMeridiemMatch[2] ?? "00");
    const meridiem = compactMeridiemMatch[3].toUpperCase();

    if (hours12 < 1 || hours12 > 12 || minutes < 0 || minutes > 59)
      return undefined;

    let hours24 = hours12 % 12;
    if (meridiem === "PM") hours24 += 12;

    return toMeridiemTime(hours24, minutes);
  }

  const timeMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (timeMatch) {
    return toMeridiemTime(Number(timeMatch[1]), Number(timeMatch[2]));
  }

  return undefined;
};

export const normalizeGroundTimingOpen = (
  value: unknown,
): string | undefined => {
  if (value == null || value === "") return undefined;

  const raw = String(value).trim();
  if (!raw) return undefined;

  const parts = raw.split(/\s*-\s*/).filter(Boolean);
  if (parts.length === 2) {
    const start = normalizeGroundTimeValue(parts[0]);
    const end = normalizeGroundTimeValue(parts[1]);

    if (start && end) return `${start}-${end}`;
    return start ?? end;
  }

  return normalizeGroundTimeValue(raw);
};

export const buildGroundTimingOpen = ({
  fromTime,
  toTime,
  timingOpen,
}: {
  fromTime?: unknown;
  toTime?: unknown;
  timingOpen?: unknown;
}): string | undefined => {
  const normalizedTiming = normalizeGroundTimingOpen(timingOpen);
  if (normalizedTiming) return normalizedTiming;

  const start = normalizeGroundTimeValue(fromTime);
  const end = normalizeGroundTimeValue(toTime);

  if (start && end) return `${start}-${end}`;
  return start ?? end;
};
